import { useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Star, Quote } from "lucide-react";
import { Reveal, RevealStagger } from "@/components/Reveal";
import { useLanguage } from "@/context/LanguageContext";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";

export default function Testimonials() {
  const { t } = useLanguage();
  const section = t("testimonials");
  const items = section.items || [];
  const carouselOpts = useMemo(() => ({ align: "start", loop: true }), []);

  const TestimonialCard = ({ review }) => (
    <Card data-testid={`testimonial-card-${review.name}`} className="bg-white rounded-2xl border border-[#e5e7e3] hover:shadow-lg transition-all duration-300 hover:-translate-y-1 h-full">
      <CardContent className="p-8">
        <Quote className="h-8 w-8 text-[#103F38]/20 mb-4" />
        <p className="text-[#152238] leading-relaxed mb-8 text-base italic">"{review.quote}"</p>
        <div className="flex items-center justify-between pt-6 border-t border-[#f0ebe3]">
          <div>
            <p className="font-semibold text-[#152238] text-sm">{review.name}</p>
            <p className="text-xs text-[#a09890]">{review.caseType}</p>
          </div>
          <div className="flex">
            {[...Array(review.rating)].map((_, j) => (
              <Star key={`star-${review.name}-${j}`} className="h-3.5 w-3.5 text-[#103F38] fill-[#103F38]" />
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <section data-testid="testimonials-section" className="py-24 md:py-32 bg-[#F5F6F3]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <Reveal className="mb-16">
          <p className="text-[#B59A65] text-xs uppercase tracking-[0.3em] mb-3 font-semibold">{section.subtitle}</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#152238] mb-4 tracking-[-0.02em]">{section.headline}</h2>
        </Reveal>

        {/* Mobile carousel */}
        <div className="md:hidden" data-testid="testimonials-carousel">
          <Carousel opts={carouselOpts} className="w-full">
            <CarouselContent className="-ml-4">
              {items.map((review) => (
                <CarouselItem key={review.name} className="pl-4 basis-[90%]">
                  <TestimonialCard review={review} />
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="flex justify-center gap-3 mt-6">
              <CarouselPrevious className="static translate-y-0 h-10 w-10 rounded-full border-[#d5d8d3] bg-white hover:bg-[#103F38] hover:text-white hover:border-[#103F38] transition-colors" />
              <CarouselNext className="static translate-y-0 h-10 w-10 rounded-full border-[#d5d8d3] bg-white hover:bg-[#103F38] hover:text-white hover:border-[#103F38] transition-colors" />
            </div>
          </Carousel>
        </div>

        {/* Desktop grid */}
        <RevealStagger className="hidden md:grid grid-cols-2 gap-6" data-testid="testimonials-grid">
          {items.map((review) => (
            <div key={review.name} className="reveal-child">
              <TestimonialCard review={review} />
            </div>
          ))}
        </RevealStagger>
      </div>
    </section>
  );
}
